import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { supabase } from "../lib/supabase";
import { useAuth } from "../lib/auth";
import { buzz } from "../lib/haptics";
import { WhiteBloom } from "../components/WhiteBloom";
import { COPY } from "../shared/copy";
import {
  lifelineState, canUseLifeline, describeLifelines, type LifelineState,
} from "../shared/lifelines";
import { QuestionTimer, useCountdown, remainingWindow } from "../components/Timer";
import type { OptionKey } from "../shared/types";

type Item = { dropQuestionId: string; position: number; isSubject: boolean; done: boolean };
type Phase = "landing" | "ready" | "countdown" | "sitout" | "question" | "locked" | "waiting" | "error" | "closed";

// Full drop play. Notification tap deep-links here but does NOT start a timer.
// The player intentionally hits LET'S GET IT then START. Timers are server-driven.
export function DropFlow() {
  const { leagueId, dropId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState<Phase>("landing");
  const [landing, setLanding] = useState<any>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [idx, setIdx] = useState(0);
  const [q, setQ] = useState<any>(null);
  const [deadline, setDeadline] = useState<string | null>(null);
  const [lockedLabel, setLockedLabel] = useState("");
  const [tick, setTick] = useState(3);
  // The blow-out between the countdown and the first question.
  const [blooming, setBlooming] = useState(false);
  // Guards the single start tap, so a second one cannot begin a second run.
  const [starting, setStarting] = useState(false);
  const [lifelineAllowance, setLifelineAllowance] = useState(0);
  const [lifelineUses, setLifelineUses] = useState<any[]>([]);
  const [status, setStatus] = useState<{ doneCount: number; memberCount: number }>({ doneCount: 0, memberCount: 0 });
  const submittedRef = useRef(false);
  const [errMsg, setErrMsg] = useState("");
  // What to run again if the person taps Try again. Play is a sequence of server
  // calls, and any one of them can drop; whichever failed is the one to retry.
  const retryRef = useRef<null | (() => void)>(null);

  // Not being able to reach the server mid-question is the worst place in the
  // app to freeze, so every call in this screen routes its failure here.
  function fail(e: any, retry: () => void) {
    const raw = String(e?.message ?? e ?? "");
    retryRef.current = retry;
    if (/not available|forbidden/i.test(raw)) {
      setPhase("closed");
      return;
    }
    setErrMsg(
      /fetch|network/i.test(raw)
        ? "We lost the connection. Your answers so far are saved."
        : "Something went wrong on our end. Your answers so far are saved."
    );
    setPhase("error");
  }

  function loadLanding() {
    setErrMsg("");
    api<any>(`/api/drops/landing`, { query: { dropId: dropId! } })
      .then((l) => {
        setLanding(l);
        setStatus({ doneCount: l.doneCount, memberCount: l.memberCount });
        if (l.seasonId && user) void loadLifelines(l.seasonId, user.id);
        if (l.status === "revealed") {
          navigate(`/league/${leagueId}/drop/${dropId}/reveal`, { replace: true });
          return;
        }
        // Already played it out: go straight to the waiting room rather than
        // pitching them the "LET'S GET IT" hero for a drop they finished.
        if (l.youAreDone) { setPhase("waiting"); return; }
        setPhase("landing");
      })
      .catch((e) => fail(e, loadLanding));
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadLanding(); }, [dropId]);

  async function begin() {
    if (starting) return;
    setStarting(true);
    try {
      await api(`/api/drops/play`, { body: { op: "begin", dropId } });
      const s = await api<{ items: Item[] }>(`/api/drops/play`, { body: { op: "status", dropId } });
      setItems(s.items);
      const firstUndone = s.items.findIndex((i) => !i.done);
      if (firstUndone === -1) { await enterWaiting(); return; }
      setIdx(firstUndone);
      enterItem(s.items, firstUndone);
    } catch (e) {
      fail(e, begin);
    } finally {
      setStarting(false);
    }
  }

  async function enterItem(list: Item[], at: number) {
    submittedRef.current = false;
    const item = list[at];
    if (!item) { await enterWaiting(); return; }
    if (item.isSubject) { setPhase("sitout"); return; }

    // Three, two, one, then the question. This runs before start-question on
    // purpose: that call is what stamps the server deadline, so counting down
    // afterwards would quietly spend the player's first three seconds.
    setTick(3);
    setPhase("countdown");
    buzz("tick");
    await new Promise<void>((resolve) => {
      let n = 3;
      const id = setInterval(() => {
        n -= 1;
        // Zero is not a beat you count, it is the shout: "LET'S GET IT" across
        // one line, then the screen blooms white into the first question.
        setTick(n);
        if (n <= 0) { clearInterval(id); window.setTimeout(resolve, 620); return; }
        buzz("tick");
      }, 700);
    });
    setBlooming(true);

    let res: any;
    try {
      res = await api<any>(`/api/drops/play`, { body: { op: "start-question", dropId, dropQuestionId: item.dropQuestionId } });
    } catch (e) {
      fail(e, () => enterItem(list, at));
      return;
    }
    if (res.status === "sitout") { setPhase("sitout"); return; }
    // The play window closed while they were in here. Sending them to a reveal
    // that has not happened yet rendered an empty "0/0" results screen.
    if (res.status === "closed") { setPhase("closed"); return; }
    if (res.status === "already") { await advance(list, at); return; }
    setQ({ ...res.question, dropQuestionId: item.dropQuestionId });
    setDeadline(res.deadlineIso);
    setPhase("question");
  }

  // What this person has left, and what they already spent on this question.
  // Loaded with the drop so the button is right the moment the question paints,
  // rather than flickering in a beat later.
  async function loadLifelines(seasonId: string, userId: string) {
    const { data: season } = await supabase
      .from("seasons").select("lifelines_per_season").eq("id", seasonId).maybeSingle();
    const { data: uses } = await supabase
      .from("lifeline_uses").select("drop_question_id, removed_options")
      .eq("season_id", seasonId).eq("user_id", userId);
    setLifelineAllowance(season?.lifelines_per_season ?? 3);
    setLifelineUses(uses ?? []);
  }

  async function useFiftyFifty() {
    if (!q) return;
    try {
      const r = await api<{ removed: OptionKey[] }>(`/api/drops/play`, {
        body: { op: "lifeline", dropId, dropQuestionId: q.dropQuestionId },
      });
      buzz("commit");
      setLifelineUses((prev) => [
        ...prev.filter((u: any) => u.drop_question_id !== q.dropQuestionId),
        { drop_question_id: q.dropQuestionId, removed_options: r.removed },
      ]);
    } catch {
      // A 50/50 that cannot be spent is not worth an error screen mid question.
      // The button simply stops offering itself on the next render.
    }
  }

  async function submit(option: OptionKey) {
    if (submittedRef.current) return;
    submittedRef.current = true;
    try {
      const res = await api<any>(`/api/drops/play`, { body: { op: "answer", dropId, dropQuestionId: q.dropQuestionId, option } });
      setLockedLabel(res.status === "timeout" ? COPY.question.time : COPY.question.locked);
      setPhase("locked");
      setTimeout(() => advance(items, idx), 700);
    } catch (e) {
      // Let them try the same answer again. Without this the guard stayed set
      // and every button on a running clock went dead.
      submittedRef.current = false;
      fail(e, () => { setPhase("question"); submit(option); });
    }
  }

  async function onTimeout() {
    if (submittedRef.current) return;
    submittedRef.current = true;
    // The server already holds a provisional timed_out row, but it doesn't know
    // the player is finished — without this ping their session never completes,
    // so the "x of y are done" count and the early reveal both stall.
    try { await api(`/api/drops/play`, { body: { op: "timeout", dropId } }); } catch { /* non-fatal */ }
    setLockedLabel(COPY.question.time);
    setPhase("locked");
    setTimeout(() => advance(items, idx), 700);
  }

  async function advanceSubject() {
    const item = items[idx];
    try {
      await api(`/api/drops/play`, { body: { op: "advance-subject", dropId, dropQuestionId: item.dropQuestionId } });
      await advance(items, idx);
    } catch (e) {
      fail(e, advanceSubject);
    }
  }

  async function advance(list: Item[], at: number) {
    const next = at + 1;
    if (next >= list.length) { await enterWaiting(); return; }
    setIdx(next);
    enterItem(list, next);
  }

  async function enterWaiting() {
    setPhase("waiting");
    await refreshWaiting();
  }

  async function refreshWaiting() {
    // Deliberately quiet on failure: this polls every ten seconds, and one
    // dropped poll is not worth throwing the player out of the waiting room.
    const l = await api<any>(`/api/drops/landing`, { query: { dropId: dropId! } });
    setStatus({ doneCount: l.doneCount, memberCount: l.memberCount });
    if (l.status === "revealed") navigate(`/league/${leagueId}/drop/${dropId}/reveal`, { replace: true });
  }

  // While waiting on the rest of the league, keep checking — otherwise a player
  // who finished first sits on a frozen "locked in" screen after results land.
  useEffect(() => {
    if (phase !== "waiting") return;
    const id = setInterval(() => { refreshWaiting().catch(() => {}); }, 10000);
    return () => clearInterval(id);
  }, [phase, dropId]);

  if (phase === "error") {
    return (
      <div className="app" data-surface="dark">
        <div className="spacer" />
        <div className="beat">
          <span className="emoji">📡</span>
          <div className="big">Lost you for a second.</div>
          <p className="muted">{errMsg}</p>
        </div>
        <div className="spacer" />
        <button className="btn primary" onClick={() => { setErrMsg(""); retryRef.current?.(); }}>Try again</button>
        <button className="btn ghost" onClick={() => navigate(`/league/${leagueId}`)}>Back to the crew</button>
      </div>
    );
  }

  if (phase === "closed") {
    return (
      <div className="app" data-surface="dark">
        <div className="spacer" />
        <div className="beat">
          <span className="emoji">⌛</span>
          <div className="big">Time&apos;s up on this one.</div>
          <p className="muted">
            The window closed. Results post once everybody is done, and your phone will let you know.
          </p>
        </div>
        <div className="spacer" />
        <button className="btn primary" onClick={() => navigate(`/league/${leagueId}`)}>Back to the crew</button>
      </div>
    );
  }

  if (!landing) return <div className="app" data-surface="dark"><div className="loading">Getting the drop…</div></div>;

  // --- Landing / pre-start ---
  //
  // One screen, one tap. This used to be two: a "let's get it" that led to a
  // second screen explaining the clock, with the real START on that one. Two
  // taps to begin something that is already a countdown reads as the first tap
  // not having worked.
  if (phase === "landing" || phase === "ready") {
    const secs = landing.questionSeconds ?? 20;
    return (
      <div className="app" data-surface="dark">
        <div className="spacer" />
        <span className="tag center">DROP {String(landing.sequence ?? "").padStart(2, "0")}</span>
        <div className="drop-hero">
          <div className="count">{landing.questionCount}</div>
          <div className="count-label">{landing.questionCount === 1 ? "question" : "questions"}</div>
          <p className="sub">{COPY.drop.aboutAMinute} · {remainingWindow(landing.closesAt)} left</p>
        </div>
        <p className="muted center">
          {secs} seconds per question, one at a time. The clock starts the second you tap.
        </p>
        <div className="spacer" />
        <button className="btn primary hype" disabled={starting} onClick={begin}>
          {starting ? "…" : COPY.drop.letsGetIt}
        </button>
      </div>
    );
  }

  // --- Three, two, one ---
  if (phase === "countdown") {
    return (
      <div className="app countdown-screen" data-surface="dark">
        {blooming && <WhiteBloom onDone={() => setBlooming(false)} />}
        {tick > 0
          ? <div className="countdown-number" key={tick}>{tick}</div>
          : <div className="countdown-go" key="go">{COPY.drop.letsGetIt}</div>}
      </div>
    );
  }

  // --- Subject sit-out ---
  if (phase === "sitout") {
    return (
      <div className="app" data-surface="dark">
        <div className="spacer" />
        <div className="beat">
          <span className="emoji">😏</span>
          <div className="big">This one&apos;s about you.</div>
          <p className="muted">{COPY.question.subjectSub}</p>
        </div>
        <div className="spacer" />
        <button className="btn primary" onClick={advanceSubject}>{COPY.question.subjectCta}</button>
      </div>
    );
  }

  // --- Locked / timeout micro-state ---
  if (phase === "locked") {
    const timedOut = lockedLabel === COPY.question.time;
    return (
      <div className="app" data-surface="dark">
        <div className="spacer" />
        <div className="beat">
          <span className="emoji">{timedOut ? "⌛" : "🔒"}</span>
          <div className="big">{lockedLabel}</div>
        </div>
        <div className="spacer" />
      </div>
    );
  }

  // --- Waiting for others ---
  if (phase === "waiting") {
    const pct = status.memberCount > 0 ? Math.round((status.doneCount / status.memberCount) * 100) : 0;
    return (
      <div className="app" data-surface="dark">
        <div className="spacer" />
        <div className="beat">
          <span className="emoji">🔒</span>
          <div className="big">{COPY.waiting.lockedIn}</div>
          <p className="muted">{COPY.waiting.resultsWhen}</p>
        </div>
        {status.memberCount > 0 && (
          <div className="card">
            <span className="tag">{COPY.waiting.doneCount(status.doneCount, status.memberCount)}</span>
            <div className="progress-track">
              <div className="progress-fill" style={{ width: `${pct}%` }} />
            </div>
            <p className="muted">Results show the second the last person locks in.</p>
          </div>
        )}
        <div className="spacer" />
        <button className="btn ghost" onClick={() => navigate(`/league/${leagueId}`)}>Back to the crew</button>
      </div>
    );
  }

  // --- Question ---
  const usedHere = lifelineUses.find((u: any) => u.drop_question_id === q?.dropQuestionId);
  const lifelines = lifelineState(
    lifelineAllowance,
    lifelineUses.length,
    (usedHere?.removed_options as OptionKey[]) ?? null
  );
  return (
    <QuestionView
      // Keyed per question, or the answer they picked stays painted on the next
      // one and the new question opens looking already answered.
      key={q?.dropQuestionId ?? idx}
      q={q} deadline={deadline} onAnswer={submit} onTimeout={onTimeout}
      index={idx} total={items.length}
      lifelines={lifelines} onLifeline={useFiftyFifty}
    />
  );
}

function QuestionView({ q, deadline, onAnswer, onTimeout, index, total, lifelines, onLifeline }: {
  q: any; deadline: string | null; onAnswer: (o: OptionKey) => void; onTimeout: () => void;
  index: number; total: number; lifelines: LifelineState; onLifeline: () => void;
}) {
  const remaining = useCountdown(deadline);
  // What they tapped, painted before the answer is even sent.
  const [picked, setPicked] = useState<OptionKey | null>(null);
  const firedRef = useRef(false);
  useEffect(() => {
    if (remaining === 0 && deadline && !firedRef.current) { firedRef.current = true; onTimeout(); }
  }, [remaining, deadline]);
  const letters: OptionKey[] = ["a", "b", "c", "d"];
  const gone = new Set(lifelines.usedHere ?? []);
  return (
    <div className="app" data-surface="dark">
      <div className="header">
        {/* Dots instead of "1 of 3": position is readable at a glance while a
            clock is running, which a sentence is not. */}
        <div className="pips" aria-label={COPY.question.progress(index + 1, total)}>
          {Array.from({ length: total }).map((_, i) => (
            <span key={i} className={`pip ${i < index ? "done" : i === index ? "current" : ""}`} />
          ))}
        </div>
        <QuestionTimer deadlineIso={deadline} />
      </div>
      <div className="spacer" />
      <h1>{q.question_text}</h1>
      <div className="answer-grid">
        {letters.map((l) => {
          // A removed option stays in place, struck through and inert, rather
          // than vanishing. If the grid reflowed, a thumb already on its way
          // down would land on a different answer than the one it aimed at.
          const removed = gone.has(l);
          return (
            <button
              key={l}
              className={
                `answer${removed ? " removed" : ""}` +
                (picked === l ? " picked" : picked ? " not-picked" : "")
              }
              disabled={removed || picked !== null}
              aria-hidden={removed}
              aria-pressed={picked === l}
              onClick={() => {
                if (removed || picked) return;
                setPicked(l);
                buzz("tick");
                onAnswer(l);
              }}
            >
              <span className="answer-key">{l.toUpperCase()}</span>
              <span>{q[`option_${l}`]}</span>
            </button>
          );
        })}
      </div>

      {lifelines.allowed > 0 && (
        <div className="lifeline-bar">
          {canUseLifeline(lifelines) ? (
            <button className="btn ghost lifeline-btn" onClick={onLifeline}>
              Use a 50/50
            </button>
          ) : null}
          <span className="muted lifeline-note">{describeLifelines(lifelines)}</span>
        </div>
      )}

      <div className="spacer" />
    </div>
  );
}
